<!--
  CurrencyInput.vue
  Form input for `currency` fields. A text input (not number: a
  native number input can't hold "$50,000"), showing the grouped amount at rest
  and the bare number while focused. Symbol follows the record's `data.currency`
  via `item`, defaulting to USD. Commits on blur/Enter, not per keystroke.
-->
<template>
  <input
    class="input"
    :class="`is-${size}`"
    type="text"
    inputmode="decimal"
    :value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autofocus="autofocus"
    @focus="onFocus"
    @input="onInput"
    @keydown.enter.prevent="onEnter"
    @blur="commit"
  />
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import type { Field } from "@auger/core/fields"
import { currencyFull, parseCurrency } from "@auger/ui/utils/number"

const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    field: Field
    item?: Record<string, any> | null
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    autofocus?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    item: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    autofocus: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: number | null]
  changed: []
}>()

const currencyCode = computed<string>(() => (props.item?.data?.currency as string) || "USD")

// Focused: show the raw `draft` (caret stays put); at rest: the grouped amount.
const focused = ref(false)
const draft = ref("")
const displayValue = computed(() => {
  if (focused.value) return draft.value
  const v = props.modelValue
  return v == null ? "" : (currencyFull(v, currencyCode.value) as string)
})

function onFocus() {
  draft.value = props.modelValue == null ? "" : String(props.modelValue)
  focused.value = true
}

function onInput(event: Event) {
  draft.value = (event.target as HTMLInputElement).value
}

// Enter ends the edit; the blur runs `commit`.
function onEnter(event: KeyboardEvent) {
  ;(event.target as HTMLInputElement).blur()
}

// One commit per edit — per-keystroke would persist every intermediate (and the
// empty step of a clear-then-retype). No-op when unchanged.
function commit() {
  focused.value = false
  const parsed = parseCurrency(draft.value)
  const current = props.modelValue == null ? null : Number(props.modelValue)
  if (parsed === current) return
  emit("update:modelValue", parsed)
  emit("changed")
}
</script>
